import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../api/supabase'

export interface ETSFRecord {
  id: string
  location: string
  record_type: string
  record_date: string
  // Z64.2: which job this record checked -- required in the UI for weekly_audit (a random
  // collection/delivery note pick), optional for whole-facility record types.
  job_ref: string | null
  inspector_name: string | null
  conducted_by: string | null
  result: string | null
  notes: string | null
  action_required: string | null
  action_completed: boolean
  action_due_date: string | null
  created_by: string | null
  created_at: string
  updated_at: string
}

export interface ETSFChecklistItem {
  id: string
  record_id: string
  category: string | null
  item_description: string
  checked: boolean
  result: string | null
  notes: string | null
}

export interface ETSFStats {
  total_this_year: number
  last_hmrc_inspection_date: string | null
  last_self_audit_date: string | null
  last_facility_check_date: string | null
  pass_count: number
  fail_count: number
  advisory_count: number
}

export function useETSFRecords(location?: string) {
  const [records, setRecords] = useState<ETSFRecord[]>([])
  const [stats, setStats] = useState<ETSFStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const loadRecords = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      let query = supabase
        .from('etsf_records')
        .select('*')
        .order('record_date', { ascending: false })

      if (location) {
        query = query.eq('location', location)
      }

      const { data, error: qErr } = await query
      if (qErr) throw qErr

      const all = (data || []) as ETSFRecord[]
      setRecords(all)

      // Calculate stats
      const now = new Date()
      const yearStart = new Date(now.getFullYear(), 0, 1)
      const thisYear = all.filter(r => new Date(r.record_date) >= yearStart)

      const lastHmrc = all.find(r => r.record_type === 'hmrc_inspection')
      const lastSelfAudit = all.find(r => r.record_type === 'self_audit')
      const lastFacility = all.find(r => r.record_type === 'facility_check')

      setStats({
        total_this_year: thisYear.length,
        last_hmrc_inspection_date: lastHmrc?.record_date || null,
        last_self_audit_date: lastSelfAudit?.record_date || null,
        last_facility_check_date: lastFacility?.record_date || null,
        pass_count: all.filter(r => r.result === 'pass').length,
        fail_count: all.filter(r => r.result === 'fail').length,
        advisory_count: all.filter(r => r.result === 'advisory').length,
      })
    } catch (e: any) {
      setError(e.message || 'Failed to load records')
    } finally {
      setLoading(false)
    }
  }, [location])

  useEffect(() => {
    loadRecords()
  }, [loadRecords])

  const createRecord = useCallback(
    async (values: Partial<ETSFRecord>) => {
      const { data, error } = await supabase
        .from('etsf_records')
        .insert([values])
        .select()
        .single()
      if (error) throw error
      await loadRecords()
      return data as ETSFRecord
    },
    [loadRecords]
  )

  const updateRecord = useCallback(
    async (id: string, values: Partial<ETSFRecord>) => {
      const { data, error } = await supabase
        .from('etsf_records')
        .update({ ...values, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single()
      if (error) throw error
      await loadRecords()
      return data as ETSFRecord
    },
    [loadRecords]
  )

  const fetchRecord = useCallback(async (id: string) => {
    const { data, error } = await supabase
      .from('etsf_records')
      .select('*')
      .eq('id', id)
      .single()
    if (error) throw error
    return data as ETSFRecord
  }, [])

  const fetchChecklistItems = useCallback(async (recordId: string) => {
    const { data, error } = await supabase
      .from('etsf_checklist_items')
      .select('*')
      .eq('record_id', recordId)
      .order('category', { ascending: true })
    if (error) throw error
    return (data || []) as ETSFChecklistItem[]
  }, [])

  const createChecklistItems = useCallback(async (recordId: string, items: Partial<ETSFChecklistItem>[]) => {
    const itemsWithRecordId = items.map(i => ({ ...i, record_id: recordId }))
    const { data, error } = await supabase.from('etsf_checklist_items').insert(itemsWithRecordId).select()
    if (error) throw error
    return data as ETSFChecklistItem[]
  }, [])

  const updateChecklistItem = useCallback(async (id: string, values: Partial<ETSFChecklistItem>) => {
    const { data, error } = await supabase
      .from('etsf_checklist_items')
      .update(values)
      .eq('id', id)
      .select()
      .single()
    if (error) throw error
    return data as ETSFChecklistItem
  }, [])

  return {
    records,
    stats,
    loading,
    error,
    reload: loadRecords,
    createRecord,
    updateRecord,
    fetchRecord,
    fetchChecklistItems,
    createChecklistItems,
    updateChecklistItem,
  }
}
